'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PageTab, type Page } from '@/modules/pages/types';
import { PageTabs } from '../PageTabs/PageTabs';
import { GridFactory } from '@/components/organisms/GridSystem';
import { useDashboard, usePage, usePageActions, useWidget } from '@/modules';
import { gridEndpoints } from '@/modules/grid/api/endpoints';
import { GridObjectFactory, IGridItem } from '@/components/organisms/GridSystem';
import { SpinningLogo } from '@/components/animations';
import { EmptyState } from './EmptyState';
import { useAuth } from '@/modules/auth';
import { useFilteredWidgets } from '@/hooks/useFilteredWidgets';
import { isDisabledByProducts, isDisabledByIncompleteProducts, PAGE_BUILDOUT_EXCLUDED_PRODUCTS } from '@/modules/pages/utils/products';

interface DashboardPageGridProps {
    page?: Page | null;
    onAddWidget: () => void;
    restoredGridContent?: number[][] | null;
    onRestoredGridContentConsumed?: () => void;
}

export function DashboardPageGrid({ page, onAddWidget, restoredGridContent, onRestoredGridContentConsumed }: DashboardPageGridProps) {
    const { state: { dealerContractedProducts, selectedDealer, selectedDateRange } } = useDashboard();
    const { state: { currentTab, currentPage } } = usePage();
    const { state: { widgets } } = useWidget();
    const { user } = useAuth();

    const { setCurrentTab } = usePageActions();

    const [gridLayout, setGridLayout] = useState<IGridItem[][]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [gridContent, setGridContent] = useState<any>(null);
    const layoutRef = useRef<any>(null);

    const pageId = page?.id;
    const userRefId = user?.userId;
    const lastPageId = useRef<number | undefined>(undefined);

    // * If the tab is disabled, it will be disabled even if it has one of the contracted products 
    const tabs = useMemo(() => {
        if (!dealerContractedProducts?.contractedProducts || !page?.tabs) {
            return []
        }
        const contractedProducts = (dealerContractedProducts?.contractedProducts || []).map(product => product.toLowerCase());
        const incompleteProducts = dealerContractedProducts?.incompleteProducts ?? [];
        return page?.tabs?.map((tab: PageTab) => {
            const disabled = tab.disabled ? true :
                isDisabledByProducts(tab?.metadata?.products, contractedProducts) ||
                isDisabledByIncompleteProducts(tab?.metadata?.products, incompleteProducts, PAGE_BUILDOUT_EXCLUDED_PRODUCTS);
            return {
                ...tab,
                label: tab.title,
                value: tab.id,
                disabled,
                icon: tab.icon,
            }
        }).sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
    }, [page?.tabs, dealerContractedProducts])


    const hasTabs = useMemo(() => tabs.length > 0, [tabs]);

    const filteredWidgets = useFilteredWidgets({ widgets, tabs, currentTab, user, page: currentPage });

    const setCurrentActiveTab = (_tabs: PageTab[]) => {
        const firstActiveTab = _tabs.findIndex(tab => !tab.disabled);
        if (firstActiveTab === -1) return;
        setCurrentTab(_tabs[firstActiveTab]);
    }


    useEffect(() => {
        if (!pageId) return;
        if (!hasTabs) return;
        if (currentTab?.id) return;
        if (tabs.length === 0) return;
        if (!dealerContractedProducts?.contractedProducts) return;
        console.log("Setting current tab", {
            tabs,
            contractedProducts: dealerContractedProducts?.contractedProducts,
        });
        setCurrentActiveTab(tabs);
    }, [pageId, hasTabs, currentTab?.id, setCurrentTab, tabs, dealerContractedProducts]);

    useEffect(() => {
        if (!pageId) return;
        if (hasTabs && !currentTab?.id) return;
        const tabExists = page?.tabs?.some(tab => tab.id === currentTab?.id);
        if (!tabExists && currentTab?.id) return;
        let canceled = false;
        setIsLoading(true);


        gridEndpoints.getGrid({
            pageId: pageId,
            pageTabId: currentTab?.id || null,
            userRefId,
        }).then(result => {
            if (canceled) return;
            const content = result?.content ?? null;
            setGridContent(content);
            layoutRef.current = content;
            setIsLoading(false);
        }).catch(() => {
            if (canceled) return;
            setGridContent(null);
            layoutRef.current = null;
            setIsLoading(false);
        });

        return () => {
            canceled = true;
        };
    }, [pageId, hasTabs, currentTab?.id, userRefId]);

    // Generate a stable key based on widget IDs for GridFactory
    const widgetsKey = useMemo(() => {
        return filteredWidgets.map(w => w.id).sort().join('-');
    }, [filteredWidgets]);

    useEffect(() => {
        if (isLoading) return;

        const content = layoutRef.current ?? gridContent;
        if (content) {
            setGridLayout(GridObjectFactory.fromDBToGrid(content, filteredWidgets));
            return;
        }

        setGridLayout(new GridObjectFactory(filteredWidgets).buildGrid());
        // Dealer and date-range deps force a fresh `gridLayout` reference on
        // those context changes — that resets GridFactory's two grid handlers
        // (display + persisted), discarding any ephemeral drags on
        // auto-arranged widgets so the user's transient layout doesn't survive
        // when the underlying data churns.
    }, [gridContent, filteredWidgets, isLoading, selectedDealer?.dealerRefId, selectedDateRange?.from, selectedDateRange?.to]);


    // * Reset current tab when page changes / if the page is not the same as the last page loaded
    useEffect(() => {

        if (page?.id !== lastPageId.current && hasTabs && tabs.length > 0) {
            lastPageId.current = page?.id;
            setCurrentActiveTab(tabs);
        }

        if (!hasTabs && currentTab?.pageId !== page?.id) {
            setCurrentTab(null);
        }
    }, [page, hasTabs]);

    useEffect(() => {
        if (!restoredGridContent) return;
        setGridContent(restoredGridContent);
        layoutRef.current = restoredGridContent;
        onRestoredGridContentConsumed?.();
    }, [restoredGridContent]);

    const handleGridChange = async (newGrid: IGridItem[][]) => {
        try {
            if (!pageId) {
                return;
            }
            const newGridContent = GridObjectFactory.fromGridToDB(newGrid);
            layoutRef.current = newGridContent;
            await gridEndpoints.saveGrid({
                pageId: pageId,
                pageTabId: currentTab?.id || null,
                content: newGridContent,
                userRefId,
            });
        } catch (error) {
            console.error('Failed to save grid:', error);
        }
    };

    return (
        <div className="py-6 w-full mb-36">
            {hasTabs && (
                <PageTabs
                    tabs={tabs}
                    widgets={filteredWidgets}
                    currentTab={currentTab || null}
                />
            )}
            {
                isLoading && (
                    <div className="flex items-center justify-center min-h-80">
                        <SpinningLogo size={64} />
                    </div>
                )
            }
            {!isLoading && filteredWidgets.length ? (
                <GridFactory
                    key={`${pageId}-${currentTab?.id || 'page'}-${widgetsKey}`}
                    grid={gridLayout}
                    widgets={filteredWidgets}
                    onChange={handleGridChange}
                />
            ) : (null)}

            {!isLoading && !filteredWidgets.length && (
                <EmptyState onAddWidget={onAddWidget} />
            )}
        </div>
    );
}
